import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import contentSeriesService from '../../services/contentSeriesService';

interface Post {
  id: string;
  title: string;
  content: string;
  publishedAt?: string;
  tags?: string[];
}

interface SeriesWithDetails {
  id: string;
  title: string;
  description?: string;
  isPublic: boolean;
  coverImage?: string;
  tags: string[];
  posts: Post[];
  stats: {
    totalPosts: number;
    totalLikes: number;
    totalComments: number;
    averageEngagement: number;
  };
  createdAt: string;
}

const SeriesViewerPage: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const [series, setSeries] = useState<SeriesWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  useEffect(() => {
    loadSeries();
  }, [seriesId]);

  const loadSeries = async () => {
    if (!seriesId) return;
    try {
      setLoading(true);
      const data = await contentSeriesService.getSeries(seriesId);
      setSeries(data);
    } catch (error) {
      console.error('Error loading series:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPost = (index: number) => {
    if (series && index >= 0 && index < series.posts.length) {
      setCurrentPostIndex(index);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevious = () => goToPost(currentPostIndex - 1);
  const goToNext = () => goToPost(currentPostIndex + 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Series not found</h2>
          <p className="text-gray-600 mb-4">This series may be private or doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const currentPost = series.posts[currentPostIndex];
  const hasPrevious = currentPostIndex > 0;
  const hasNext = currentPostIndex < series.posts.length - 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900 inline-flex items-center text-sm"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="text-sm text-gray-600">
              Post {currentPostIndex + 1} of {series.posts.length}
            </div>
          </div>

          {series.coverImage && (
            <img
              src={series.coverImage}
              alt={series.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
          )}

          <h1 className="text-3xl font-bold text-gray-900 mb-2">{series.title}</h1>
          {series.description && (
            <p className="text-gray-600 mb-4">{series.description}</p>
          )}

          {series.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {series.tags.map((tag, i) => (
                <span
                  key={i}
                  className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentPostIndex + 1) / series.posts.length) * 100}%` }}
            />
          </div>

          {/* Stats */}
          <div className="flex gap-6 text-sm text-gray-600">
            <span>📚 {series.stats.totalPosts} posts</span>
            <span>❤️ {series.stats.totalLikes} likes</span>
            <span>💬 {series.stats.totalComments} comments</span>
          </div>
        </div>
      </div>

      {/* Current Post Content */}
      {currentPost && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <article className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {currentPost.title || 'Untitled'}
            </h2>

            {currentPost.publishedAt && (
              <p className="text-sm text-gray-500 mb-6">
                Published on {new Date(currentPost.publishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}

            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">{currentPost.content}</p>
            </div>
          </article>

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={goToPrevious}
              disabled={!hasPrevious}
              className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                hasPrevious
                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            <button
              onClick={hasNext ? goToNext : () => navigate('/')}
              className="inline-flex items-center px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {hasNext ? (
                <>
                  Next
                  <svg className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              ) : (
                'Finish Series'
              )}
            </button>
          </div>

          {/* Series Posts List */}
          <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">All Posts in This Series</h3>
            <div className="space-y-3">
              {series.posts.map((post, index) => (
                <button
                  key={post.id}
                  onClick={() => goToPost(index)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    index === currentPostIndex
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      index === currentPostIndex ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-medium text-gray-900 truncate">
                        {post.title || 'Untitled'}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{post.content}</p>
                    </div>
                    {index === currentPostIndex && (
                      <span className="flex-shrink-0 text-xs font-medium text-blue-600">
                        Current
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {series.posts.length === 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No posts yet</h3>
          <p className="mt-1 text-sm text-gray-500">This series doesn't have any posts yet.</p>
        </div>
      )}
    </div>
  );
};

export default SeriesViewerPage;
